/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-30
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../api/baseQuery';
import type {
  BackendPaymentMethod,
  PaymentTransaction,
  BillingDashboard,
  // PaymentSetupIntent, // TODO: Use when setup intent endpoints are implemented
} from '../../types/payment';

export const paymentApi = createApi({
  reducerPath: 'paymentApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['PaymentMethod', 'Transaction', 'BillingDashboard'],
  endpoints: builder => ({
    // Payment Methods
    getPaymentMethods: builder.query<
      {
        count: number;
        next: string | null;
        previous: string | null;
        results: BackendPaymentMethod[];
      },
      void
    >({
      query: () => 'payment-methods/',
      providesTags: ['PaymentMethod'],
    }),

    addPaymentMethod: builder.mutation<
      {
        payment_method: BackendPaymentMethod;
        message: string;
        beta_mode: boolean;
      },
      { stripe_payment_method_id: string; set_as_default?: boolean }
    >({
      query: data => ({
        url: 'payment-methods/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['PaymentMethod', 'BillingDashboard'],
    }),

    deletePaymentMethod: builder.mutation<{ message: string }, string>({
      query: id => ({
        url: `payment-methods/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PaymentMethod', 'BillingDashboard'],
    }),

    setDefaultPaymentMethod: builder.mutation<{ message: string }, string>({
      query: id => ({
        url: `payment-methods/${id}/set_default/`,
        method: 'POST',
      }),
      invalidatesTags: ['PaymentMethod', 'BillingDashboard'],
    }),

    // Payment Setup
    createSetupIntent: builder.mutation<
      {
        client_secret: string;
        setup_intent_id: string;
        beta_mode: boolean;
        message: string;
      },
      Record<string, any>
    >({
      query: data => ({
        url: 'payment-setup/',
        method: 'POST',
        body: data,
      }),
    }),

    // Billing Dashboard
    getBillingDashboard: builder.query<BillingDashboard, void>({
      query: () => 'billing-dashboard/',
      providesTags: ['BillingDashboard'],
    }),

    // Billing Information
    getBillingInfo: builder.query<
      {
        payment_methods: BackendPaymentMethod[];
        transactions: PaymentTransaction[];
        subscription: {
          plan: string;
          status: string;
          trial_active: boolean;
        };
        beta_mode: boolean;
      },
      void
    >({
      query: () => 'billing/',
      providesTags: ['PaymentMethod', 'Transaction'],
    }),

    // Transaction History
    getTransactionHistory: builder.query<
      {
        results: PaymentTransaction[];
        count: number;
        next: string | null;
        previous: string | null;
      },
      { page?: number; page_size?: number }
    >({
      query: ({ page = 1, page_size = 10 } = {}) =>
        `transactions/?page=${page}&page_size=${page_size}`,
      providesTags: ['Transaction'],
    }),

    getTransactionSummary: builder.query<
      {
        total_transactions: number;
        successful_transactions: number;
        failed_transactions: number;
        total_amount_cents: number;
        total_amount_display: string;
        beta_mode: boolean;
      },
      void
    >({
      query: () => 'transactions/summary/',
      providesTags: ['Transaction'],
    }),

    // Billing Address Management
    getBillingAddress: builder.query<
      {
        id: string;
        name: string;
        line1: string;
        line2?: string;
        city: string;
        state: string;
        postal_code: string;
        country: string;
      },
      string
    >({
      query: paymentMethodId =>
        `payment-methods/${paymentMethodId}/billing-address/`,
      providesTags: ['PaymentMethod'],
    }),

    updateBillingAddress: builder.mutation<
      {
        message: string;
        billing_address: {
          id: string;
          name: string;
          line1: string;
          line2?: string;
          city: string;
          state: string;
          postal_code: string;
          country: string;
        };
      },
      {
        paymentMethodId: string;
        billingData: {
          name: string;
          line1: string;
          line2?: string;
          city: string;
          state: string;
          postal_code: string;
          country: string;
        };
      }
    >({
      query: ({ paymentMethodId, billingData }) => ({
        url: `payment-methods/${paymentMethodId}/billing-address/`,
        method: 'PATCH',
        body: billingData,
      }),
      invalidatesTags: ['PaymentMethod', 'BillingDashboard'],
    }),

    // Process Payment
    processPayment: builder.mutation<
      { message: string; transaction: PaymentTransaction; beta_mode: boolean },
      { amount: number; description?: string }
    >({
      query: data => ({
        url: 'billing/process_payment/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Transaction', 'BillingDashboard'],
    }),
  }),
});

export const {
  useGetPaymentMethodsQuery,
  useAddPaymentMethodMutation,
  useDeletePaymentMethodMutation,
  useSetDefaultPaymentMethodMutation,
  useCreateSetupIntentMutation,
  useGetBillingDashboardQuery,
  useGetBillingInfoQuery,
  useGetTransactionHistoryQuery,
  useGetTransactionSummaryQuery,
  useProcessPaymentMutation,
  useGetBillingAddressQuery,
  useUpdateBillingAddressMutation,
} = paymentApi;
